import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Box, Button, ButtonText, Center, HStack, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

const AVATAR_SIZE = 200;

/* Round avatar preview — parent Box clips to a circle via borderRadius="$full". */
const ROUND_PREVIEW_VIDEO_STYLE = {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  objectFit: 'cover' as const,
  display: 'block' as const,
  transform: 'scaleX(-1)' as const,
};

const ROUND_PREVIEW_IMAGE_STYLE = {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  objectFit: 'cover' as const,
  display: 'block' as const,
  transform: 'scaleX(-1)' as const,
};

export type LecturerPresenceSetupProps = {
  onConfirm: (args: { imageBase64: string; saveAsProfile: boolean }) => void;
  savedPhotoUrl?: string | null;
  /** True while the backend is still resolving whether a saved photo exists. */
  savedPhotoResolving?: boolean;
  /** Avatar GET failed — show retry instead of opening the camera. */
  savedPhotoLoadFailed?: boolean;
  onRetrySavedPhotoLoad?: () => void;
  /** Pre-flight supervisors column camera — capture from it when already sending. */
  externalCaptureVideoRef?: React.RefObject<HTMLVideoElement | null>;
};

/** Constrain the card so buttons don't stretch to the full panel width. */
const CARD_MAX_WIDTH = 420;

/** Sentinel sent when the lecturer keeps an existing saved avatar without re-uploading. */
export const LECTURER_SAVED_PHOTO_PLACEHOLDER = 'saved-photo-placeholder';

/**
 * Feature-detect rather than platform-branch: oral exams are hardwired to web
 * (the native app is blocked upstream by `OralExamMobileAppNotice`), so the
 * only reason this would ever be false in practice is a browser without
 * media APIs — the same condition that already governs whether the effect
 * below can even ask for a camera stream.
 */
function isCameraAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/** True when an external video element has a live frame worth capturing. */
export function isExternalCaptureVideoReady(
  video: HTMLVideoElement | null | undefined,
): boolean {
  if (!video) return false;
  const stream = video.srcObject as { getVideoTracks?: () => MediaStreamTrack[] } | null;
  const track = stream?.getVideoTracks?.()[0];
  return Boolean(
    video.readyState >= 2
    && track?.readyState === 'live'
    && !track.muted,
  );
}

/** Captures JPEG base64 from a video element. Exported for unit tests. */
export async function captureStillFromVideo(video: HTMLVideoElement): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || 640;
  canvas.height = video.videoHeight || 480;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  return dataUrl.split(',')[1] ?? '';
}

/** Unit-testable capture when camera is unavailable. */
export function captureSelfieWithoutCamera(): string {
  return 'selfie-capture-placeholder';
}

/**
 * Lecturer group-briefing avatar capture: round supervisor photo before entering
 * the live briefing room (GB-UX1 v0.7).
 *
 * This step is offline. It does not join the pre-flight room. The preview is a
 * local camera, and that stream stops as soon as the still is captured or the
 * component unmounts. The supervisors column appears only on the pre-flight
 * screen after the lecturer confirms the photo. An optional external video
 * element can supply the still in tests; the setup screen does not pass one.
 */
export function LecturerPresenceSetup({
  onConfirm,
  savedPhotoUrl,
  savedPhotoResolving,
  savedPhotoLoadFailed,
  onRetrySavedPhotoLoad,
  externalCaptureVideoRef,
}: LecturerPresenceSetupProps) {
  const { t } = useTranslation();
  const cameraReady = isCameraAvailable();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraLive, setCameraLive] = useState(false);
  const [capturedBase64, setCapturedBase64] = useState<string | null>(null);
  const [isRetaking, setIsRetaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A saved avatar exists only when the backend actually resolved a URL we can
  // render: we must never claim "Saved profile photo" while showing nothing, or the
  // lecturer can't decide whether to reuse it. If the URL can't be resolved we fall
  // through to the capture flow. `savedPhotoResolving` covers the brief window before
  // the answer is known so we neither flash the camera nor guess.
  const resolvingSaved = Boolean(savedPhotoResolving) && !isRetaking && !capturedBase64;
  const avatarLoadBlocked = Boolean(savedPhotoLoadFailed) && !isRetaking && !capturedBase64;
  const hasSavedPhoto = Boolean(savedPhotoUrl) && !isRetaking;
  const showPreview = Boolean(capturedBase64) || hasSavedPhoto;

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraLive(false);
  }, []);

  const externalCaptureReady = isExternalCaptureVideoReady(
    externalCaptureVideoRef?.current ?? null,
  );

  // Open the local camera while we still need to capture (not showing a preview,
  // not still waiting to learn whether a saved photo exists, and no column track).
  useEffect(() => {
    if (!cameraReady || showPreview || resolvingSaved || avatarLoadBlocked || externalCaptureReady) {
      return undefined;
    }
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        setCameraLive(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [avatarLoadBlocked, cameraReady, externalCaptureReady, showPreview, resolvingSaved]);

  useEffect(() => {
    if (externalCaptureReady) {
      stopStream();
    }
  }, [externalCaptureReady, stopStream]);

  // Bind the live stream to the on-screen <video> element.
  useEffect(() => {
    const video = videoRef.current;
    if (video && streamRef.current && cameraLive && !showPreview) {
      video.srcObject = streamRef.current;
      video.play?.().catch(() => {});
    }
  }, [cameraLive, showPreview]);

  // Release the camera when the component unmounts.
  useEffect(() => stopStream, [stopStream]);

  const handleTakePhoto = useCallback(async () => {
    setError(null);
    try {
      let imageBase64: string;
      const externalVideo = externalCaptureVideoRef?.current ?? null;
      if (isExternalCaptureVideoReady(externalVideo)) {
        imageBase64 = await captureStillFromVideo(externalVideo as HTMLVideoElement);
      } else {
        const stream = streamRef.current;
        if (cameraReady && stream) {
          const captureEl = document.createElement('video') as HTMLVideoElement;
          captureEl.srcObject = stream;
          captureEl.muted = true;
          try {
            await captureEl.play?.();
          } catch {
          /* autoplay may reject; a current frame is still capturable */
          }
          await new Promise<void>((resolve) => {
            if (captureEl.readyState >= (HTMLMediaElement?.HAVE_CURRENT_DATA ?? 2)) {
              resolve();
              return;
            }
            captureEl.onloadeddata = () => resolve();
          });
          imageBase64 = await captureStillFromVideo(captureEl);
          captureEl.srcObject = null;
        } else {
          imageBase64 = captureSelfieWithoutCamera();
        }
      }
      setCapturedBase64(imageBase64);
      setIsRetaking(false);
      stopStream();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [cameraReady, externalCaptureVideoRef, stopStream]);

  const handleRetake = useCallback(() => {
    setCapturedBase64(null);
    setIsRetaking(true);
    setError(null);
  }, []);

  const usingExistingSavedPhoto = hasSavedPhoto && !capturedBase64;

  const handleConfirm = useCallback(() => {
    const imageBase64 = capturedBase64 ?? (hasSavedPhoto ? LECTURER_SAVED_PHOTO_PLACEHOLDER : '');
    if (!imageBase64) return;
    stopStream();
    onConfirm({
      imageBase64,
      saveAsProfile: false,
    });
  }, [
    capturedBase64,
    hasSavedPhoto,
    onConfirm,
    stopStream,
  ]);

  const checklistItems = [
    {
      key: 'avatarReady',
      label: t('oralExam.lecturer.presenceSetup.checklistAvatarReady', {
        defaultValue: 'Avatar ready',
      }),
      done: showPreview,
    },
    {
      key: 'briefingLive',
      label: t('oralExam.lecturer.presenceSetup.checklistBriefingLive', {
        defaultValue: 'Briefing is live before recording',
      }),
      done: false,
    },
    {
      key: 'examNext',
      label: t('oralExam.lecturer.presenceSetup.checklistExamNext', {
        defaultValue: 'Exam starts next',
      }),
      done: false,
    },
  ];

  return (
    <VStack
      space="md"
      p="$4"
      width="100%"
      maxWidth={CARD_MAX_WIDTH}
      alignSelf="center"
      testID="lecturer-presence-setup"
    >
      <VStack space="xs">
        <Text size="lg" color="$textHeading" fontWeight="bold">
          {t('oralExam.lecturer.presenceSetup.title', {
            defaultValue: 'Set up group briefing',
          })}
        </Text>
        <Text size="sm" color="$textBody">
          {t('oralExam.lecturer.presenceSetup.body', {
            defaultValue: 'Capture the round supervisor avatar students will see during the exam, then enter the briefing room. Students stay in the waiting room until you enter.',
          })}
        </Text>
      </VStack>

      <Center py="$2">
        <Box
          width={AVATAR_SIZE}
          height={AVATAR_SIZE}
          borderRadius="$full"
          overflow="hidden"
          borderWidth={1}
          borderColor="$borderLight"
          bg="$bgSubtle"
          alignItems="center"
          justifyContent="center"
          testID="presence-avatar-frame"
        >
          {showPreview ? (
            <Box
              width={AVATAR_SIZE}
              height={AVATAR_SIZE}
              alignItems="center"
              justifyContent="center"
              testID="presence-preview"
            >
              {capturedBase64 ? (
                <img
                  src={`data:image/jpeg;base64,${capturedBase64}`}
                  alt=""
                  style={ROUND_PREVIEW_IMAGE_STYLE}
                />
              ) : (
                <Box testID="presence-saved-photo">
                  <img src={savedPhotoUrl ?? ''} alt="" style={ROUND_PREVIEW_IMAGE_STYLE} />
                </Box>
              )}
            </Box>
          ) : resolvingSaved ? (
            <Text size="xs" color="$textHint" textAlign="center" px="$3">
              {t('oralExam.lecturer.presenceSetup.checkingSaved', {
                defaultValue: 'Checking for a saved photo…',
              })}
            </Text>
          ) : avatarLoadBlocked ? (
            <VStack space="sm" alignItems="center" px="$3" testID="presence-avatar-load-failed">
              <Text size="xs" color="$statusError" textAlign="center">
                {t('oralExam.lecturer.presenceSetup.avatarLoadFailed', {
                  defaultValue: 'Could not load your saved avatar. Check your connection and try again.',
                })}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onPress={onRetrySavedPhotoLoad}
                testID="presence-avatar-retry"
              >
                <ButtonText>
                  {t('oralExam.lecturer.presenceSetup.avatarLoadRetry', {
                    defaultValue: 'Try again',
                  })}
                </ButtonText>
              </Button>
            </VStack>
          ) : cameraReady && cameraLive ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={ROUND_PREVIEW_VIDEO_STYLE}
            />
          ) : (
            <Text size="xs" color="$textHint" textAlign="center" px="$3">
              {cameraReady && !cameraLive
                ? t('oralExam.lecturer.presenceSetup.preparingCamera', {
                  defaultValue: 'Preparing camera…',
                })
                : t('oralExam.lecturer.presenceSetup.previewPlaceholder', {
                  defaultValue: 'Camera preview will appear here',
                })}
            </Text>
          )}
        </Box>
      </Center>

      {!!error && (
        <Text size="xs" color="$statusError">{error}</Text>
      )}

      {resolvingSaved || avatarLoadBlocked ? null : showPreview ? (
        <Button
          size="sm"
          variant="outline"
          onPress={handleRetake}
          testID="presence-retake"
        >
          <ButtonText>
            {usingExistingSavedPhoto
              ? t('oralExam.lecturer.presenceSetup.update', {
                defaultValue: 'Update avatar photo',
              })
              : t('oralExam.lecturer.presenceSetup.retake', {
                defaultValue: 'Retake avatar photo',
              })}
          </ButtonText>
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onPress={handleTakePhoto}
          isDisabled={cameraReady && !cameraLive}
          testID="presence-take-photo"
        >
          <ButtonText>
            {t('oralExam.lecturer.presenceSetup.takePhoto', {
              defaultValue: 'Take avatar photo',
            })}
          </ButtonText>
        </Button>
      )}

      <VStack space="xs" testID="presence-prep-checklist">
        {checklistItems.map((item) => (
          <HStack key={item.key} alignItems="center" space="xs">
            <Box
              w={6}
              h={6}
              borderRadius="$full"
              bg={item.done ? '$statusSuccess' : '$borderMedium'}
            />
            <Text size="xs" color={item.done ? '$textBody' : '$textSubtle'}>
              {item.label}
            </Text>
          </HStack>
        ))}
      </VStack>

      <Button
        size="sm"
        action="primary"
        onPress={handleConfirm}
        isDisabled={!showPreview}
        testID="presence-confirm"
      >
        <ButtonText>
          {t('oralExam.lecturer.presenceSetup.confirm', {
            defaultValue: 'Use photo and go to exam pre-flight check',
          })}
        </ButtonText>
      </Button>
    </VStack>
  );
}
